import { useEffect, useRef, useState } from "react";
import { Form, Formik, Field } from "formik";
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import * as Yup from 'yup';
import { BeatLoader } from 'react-spinners';
import BuildForms from './build-forms';
import BuildSchema from "./build-schema";
import FormFooter from "../../../components/form-footer";
import IntakeGeneralInformationFields from "../../../components/intake/general-information-fields";
import IntakeAdditionalInformationFields from "../../../components/intake/additional-information-fields";
import IntakeTermsAndConditionsFields from "../../../components/intake/terms-and-conditions-fields";
import { selectAllActiveForm, selectActiveFormInvestigationType, resetState, submitForm, selectActiveFormExistingOrder } from "../../../reducers/active-form-slice";
import { resetAdditionalInformationFilesStatus, selectAllAdditionalInformationFiles, uploadAdditionalInformationFiles, deleteAdditionalInformationFiles, resetAdditionalInformationFiles } from '../../../reducers/additional-information-files-slice';
import { resetSubpoenaDetailsFilesStatus, selectAllSubpoenaDetailsFiles, uploadSubpoenaDetailsFiles, deleteSubpoenaDetailsFiles, resetSubpoenaDetailsFiles } from '../../../reducers/subpoena-details-files-slice';
import { resetSWITRSDetailsFilesStatus, selectAllSWITRSDetailsFiles, uploadSWITRSDetailsFiles, deleteSWITRSDetailsFiles, resetSWITRSDetailsFiles } from '../../../reducers/switrs-details-files-slice';
import { resetTrafficCollisionsReportRetrievalFilesStatus, selectAllTrafficCollisionsReportRetrievalFiles, uploadTrafficCollisionsReportRetrievalFiles, deleteTrafficCollisionsReportRetrievalFiles, resetTrafficCollisionsReportRetrievalFiles } from '../../../reducers/traffic-collisions-report-retrieval-files-slice';
import SubmissionFailureModal from '../../../components/modals/submission-failure';
import { deleteFaultInfoFiles, resetFaultInfoFilesStatus, selectAllFaultInfoFiles, uploadFaultInfoFiles } from '../../../reducers/fault-info-files-slice';
import { resetAttachmentsAfterSubmission } from "../../../reducers/attachments-slice";
import { selectAdUserAcceptedTermsAndConditions, fetchAuth, selectAdUserPermissionTitle } from '../../../reducers/auth-slice';
import TermsAndConditionsModal from '../../../components/modals/terms-and-conditions';
import FormHeaderButtons from '../form-header-buttons';
import { selectOrder, updateOrder } from '../../../reducers/order-details-slice';
import { useBuildUpdatedDraftOrder } from '../../../utility/build-updated-draft-order';
import FormService from '../../../services/form-service';
import OrderService from '../../../services/orders-service';


const IntakeConfirmation = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const formRef = useRef();
    const [submitType, setSubmitType] = useState();
    const acceptedTermsAndConditions = useSelector(selectAdUserAcceptedTermsAndConditions);
    const permissionTitle = useSelector(selectAdUserPermissionTitle);
    const [termsAndConditionsAccepted, setTermsAndConditionsAccepted] = useState(acceptedTermsAndConditions ?? false);
    const [formValues, setFormValues] = useState(null);
    const order = useSelector(selectOrder);
    const orderStatus = useSelector(state => state.orderDetails.status);
    const isExistingOrder = useSelector(selectActiveFormExistingOrder);
    const { buildUpdatedDraftOrder, uploadAttachments } = useBuildUpdatedDraftOrder();
    const [showOrderServiceAgreement, setShowOrderServiceAgreement] = useState(false);
    const [nextOrderNumber, setNextOrderNumber] = useState("");

    const toggleServiceAgreementModal = () => {
        setShowOrderServiceAgreement(!showOrderServiceAgreement);
    }

    /**
     * get current form values
     */
    const allActiveFormValues = useSelector(selectAllActiveForm);
    const investigationType = useSelector(selectActiveFormInvestigationType);


    /**
     * handle the previous button
     */
    const handlePrevious = () => {
        navigate("/intake/additional-information");
    };

    /**
     * handle the next button
     */
    const handleNext = async (e) => {
        e.preventDefault();
        await dispatch(resetState());
        await dispatch(resetSubpoenaDetailsFiles());
        await dispatch(resetSWITRSDetailsFiles());
        await dispatch(resetAdditionalInformationFiles());
        await dispatch(resetTrafficCollisionsReportRetrievalFiles());
        await dispatch(resetAttachmentsAfterSubmission());
        await dispatch(resetFaultInfoFilesStatus());
        navigate("/portal");
    };

    /**
     * terms and conditions modal
     */
    const [showTermsAndConditionsModal, setShowTermsAndConditionsModal] = useState(false);  

    const handleCloseTermsAndConditionsModal = async (accepted) => {
        setShowTermsAndConditionsModal(false);      
        if (accepted && formValues) {
            await dispatch(fetchAuth());
            if (isExistingOrder) {
                const updatedOrder = buildUpdatedDraftOrder(formValues, true);
                await dispatch(updateOrder(updatedOrder));
                await uploadAttachments();
            } else {
                await dispatch(submitForm(formValues));
            }
        }
    };

    /**
     * update active form & continue or fail
     */
    const activeFormStatus = useSelector(state => state.activeForm.status);

    const handleSubmit = async (values) => {
        // only show the modal for case managers who have not accepted the terms and conditions
        if (!termsAndConditionsAccepted && (!permissionTitle || permissionTitle.includes("Case Manager"))) {
            setFormValues(values);  
            setShowTermsAndConditionsModal(true);   
        } else {
            if (isExistingOrder) {
                const updatedOrder = buildUpdatedDraftOrder(values, true);
                await dispatch(updateOrder(updatedOrder));
                await uploadAttachments();
            } else {
                await dispatch(submitForm(values));
            }
        }
    }; 

    useEffect(() => {
        async function fetchNextOrderNumber() {
            var nextOrder = await OrderService.GetNextOrderNumber(allActiveFormValues)
            setNextOrderNumber(nextOrder);  
        }

        if (allActiveFormValues && allActiveFormValues.generalInformation.firstNameClient) {
            fetchNextOrderNumber();            
        }

    }, [allActiveFormValues])

    useEffect(() => {
        if (activeFormStatus === "succeeded" || orderStatus === "succeeded") {
            (async () => {                
                await dispatch(resetState());
                await dispatch(resetSubpoenaDetailsFiles());
                await dispatch(resetSWITRSDetailsFiles());
                await dispatch(resetAdditionalInformationFiles());
                await dispatch(resetTrafficCollisionsReportRetrievalFiles());
                await dispatch(resetFaultInfoFilesStatus());
                await dispatch(resetAttachmentsAfterSubmission());

                if (submitType === 'submitContinue') {
                    navigate('/intake/success');
                } else {
                    navigate(`/intake/general-information?caseName=${allActiveFormValues.generalInformation.firstNameClient} ${allActiveFormValues.generalInformation.lastNameClient} ${allActiveFormValues.generalInformation.fileNumberClient}`);
                }
            })();
        } // Submission failure is handled through FormHeaderButtons
    }, [activeFormStatus, orderStatus, submitType, navigate, dispatch]);


    // Sending order confirmation email for orders that originally existed as a draft
    useEffect(() => {
        if (orderStatus === "succeeded") {
            (async () => {   
                await FormService.SendOrderConfirmationEmail(false, [allActiveFormValues.generalInformation.emailPrimary], order.details.orderNumber, allActiveFormValues.generalInformation.firstNameCaseManager);
            })();
        }
    }, [orderStatus])


    /**
     * setup the validation
     */
    const schema = BuildSchema(investigationType);
    const validationSchema = Yup.object().shape({
        ...schema
    });


    /**
     * handle the file upload for additional information
     */
    const additionalInformationFiles = useSelector(selectAllAdditionalInformationFiles);
    const additionalInformationFilesStatus = useSelector(state => state.additionalInformationFiles.status);

    const handleAdditionalFilesUpload = async files => {
        await dispatch(uploadAdditionalInformationFiles(files));
    };

    const handleUploadedAdditionalFilesRemoval = async guid => {
        await dispatch(deleteAdditionalInformationFiles(guid));
    };

    useEffect(() => {
        if (additionalInformationFilesStatus === "succeeded") {
            formRef.current.setFieldValue('additionalInformation.files', additionalInformationFiles.additionalInformationFiles);

            (async () => {
                await dispatch(resetAdditionalInformationFilesStatus());
            })();
        }
    }, [additionalInformationFiles, additionalInformationFilesStatus, dispatch]);

    /**
     * handle the file upload for Subpoena Process Service
     */
    const subpoenaDetailsFiles = useSelector(selectAllSubpoenaDetailsFiles);
    const subpoenaDetailsFilesStatus = useSelector(state => state.subpoenaDetailsFiles.status);

    const handleSubpoenaUpload = async files => {
        await dispatch(uploadSubpoenaDetailsFiles(files));
    };

    const handleSubpoenaUploadedRemoval = async guid => {
        await dispatch(deleteSubpoenaDetailsFiles(guid));
    };

    useEffect(() => {
        if (subpoenaDetailsFilesStatus === "succeeded") {
            formRef.current.setFieldValue('subpoenaDetails.files', subpoenaDetailsFiles.subpoenaDetailsFiles);

            (async () => {
                await dispatch(resetSubpoenaDetailsFilesStatus());
            })();
        }
    }, [subpoenaDetailsFiles, subpoenaDetailsFilesStatus, dispatch]);


    /**
     * handle the file upload for SWITRS
     */
    const switrsDetailsFiles = useSelector(selectAllSWITRSDetailsFiles);
    const switrsDetailsFilesStatus = useSelector(state => state.switrsDetailsFiles.status);

    const handleSWITRSUpload = async files => {
        await dispatch(uploadSWITRSDetailsFiles(files));
    };

    const handleSWITRSUploadedRemoval = async guid => {
        await dispatch(deleteSWITRSDetailsFiles(guid));
    };

    useEffect(() => {
        if (switrsDetailsFilesStatus === "succeeded") {
            formRef.current.setFieldValue('switrsDetails.files', switrsDetailsFiles.switrsDetailsFiles);

            (async () => {
                await dispatch(resetSWITRSDetailsFilesStatus());
            })();
        }
    }, [switrsDetailsFiles, switrsDetailsFilesStatus, dispatch]);


    /**
     * handle the file upload for Traffic Collisions Report Retrieval
     */
    const trafficCollisionsReportRetrievalFiles = useSelector(selectAllTrafficCollisionsReportRetrievalFiles);
    const trafficCollisionsReportRetrievalFilesStatus = useSelector(state => state.trafficCollisionsReportRetrievalFiles.status);

    const handleTrafficCollisionsReportRetrievalUpload = async files => {
        await dispatch(uploadTrafficCollisionsReportRetrievalFiles(files));
    };

    const handleTrafficCollisionsReportRetrievalUploadedRemoval = async guid => {
        await dispatch(deleteTrafficCollisionsReportRetrievalFiles(guid));
    };

    useEffect(() => {
        if (trafficCollisionsReportRetrievalFilesStatus === "succeeded") {
            formRef.current.setFieldValue('trafficCollisionsReportRetrievalDetails.files', trafficCollisionsReportRetrievalFiles.trafficCollisionsReportRetrievalFiles);

            (async () => {
                await dispatch(resetTrafficCollisionsReportRetrievalFilesStatus());
            })();
        }
    }, [trafficCollisionsReportRetrievalFiles, trafficCollisionsReportRetrievalFilesStatus, dispatch]);

    return (
        <div className="page confirmation">
            {(activeFormStatus === "loading" || orderStatus === "loading" || additionalInformationFilesStatus === 'loading' || switrsDetailsFilesStatus === 'loading' || trafficCollisionsReportRetrievalFilesStatus === 'loading') &&
                <div className="loader-container">
                    <BeatLoader color="#00818C" />
                </div>
            }

            <Formik
                innerRef={formRef}
                initialValues={allActiveFormValues}
                validationSchema={validationSchema}
                onSubmit={handleSubmit}
            >
                {({ errors, isValid, touched, values }) => (
                    <Form>
                        <div className="form-header-buttons">
                            <div>
                                <h2>Please confirm that your information is correct.</h2>
                            </div>
                            <FormHeaderButtons updateForm={null} showSaveForLater={false}/>
                        </div>
                
                        <IntakeGeneralInformationFields
                            errors={errors.generalInformation}
                            touched={touched.generalInformation}
                            values={values.generalInformation}
                            nested={true}
                            disabled={isExistingOrder}
                        />


                        <BuildForms
                            errors={errors}
                            touched={touched}
                            values={values}
                            investigationType={investigationType}

                            handleUpload={
                                investigationType[1] === "918a0ae1-4e91-49a2-a595-190f6b87e8dc" ?
                                    handleSubpoenaUpload : investigationType[1] === "c7537487-e799-49f0-85b9-13c07170ba20" ?
                                    handleSWITRSUpload :
                                        handleTrafficCollisionsReportRetrievalUpload 
                            }
                            handleUploadedReAdditionalFilesmoval={
                                investigationType[1] === "918a0ae1-4e91-49a2-a595-190f6b87e8dc" ?
                                    handleSubpoenaUploadedRemoval : investigationType[1] === "c7537487-e799-49f0-85b9-13c07170ba20" ?
                                    handleSWITRSUploadedRemoval :
                                        handleTrafficCollisionsReportRetrievalUploadedRemoval
                            }
                            uploadedFiles={
                                investigationType[1] === "918a0ae1-4e91-49a2-a595-190f6b87e8dc" ?
                                    values.subpoenaProcessServingDetails.files : investigationType[1] === "c7537487-e799-49f0-85b9-13c07170ba20" ?
                                    values.switrsDetails.files : investigationType[1] === "26a9c1ea-45a1-4181-8665-9cdc33721f71" ?
                                        values.trafficCollisionsReportRetrievalDetails.files : values.witnessDefendantStatementFaultInfo.files
                            }
                            uploadStatus={
                                investigationType[1] === "918a0ae1-4e91-49a2-a595-190f6b87e8dc" ?
                                    subpoenaDetailsFilesStatus : investigationType[1] === "c7537487-e799-49f0-85b9-13c07170ba20" ?
                                    switrsDetailsFilesStatus :
                                        trafficCollisionsReportRetrievalFilesStatus
                            }
                        />

                        <IntakeAdditionalInformationFields
                            handleUpload={handleAdditionalFilesUpload}
                            handleUploadedRemoval={handleUploadedAdditionalFilesRemoval}
                            uploadedFiles={values.additionalInformation.files}
                            uploadStatus={additionalInformationFilesStatus}
                            nested={true}
                        />

                        {investigationType[1] !== "dd7b99f9-5394-4d46-87f3-3a0804a8738a" && (
                            <div className="form-footer-rush">
                                    <div className="form-check">
                                        <Field type="checkbox" name="rushOrder" id="rushOrder" />
                                    </div>
                                    <label htmlFor="rushOrder">Rush Order (Additional fees may apply)</label>
                            </div>
                        )}

                        <FormFooter
                            handlePrevious={handlePrevious}
                            showPrevious={true}
                            disableNext={true}
                            showNext={false}
                            progress={`100`}
                        />

                        <IntakeTermsAndConditionsFields
                            errors={errors.acceptTerms}
                            touched={touched.acceptTerms}
                            values={values.acceptTerms}
                            nested={false}
                            showModal={showOrderServiceAgreement}
                            toggleModal={toggleServiceAgreementModal}
                            generalInformation={values.generalInformation}
                            orderNumber={nextOrderNumber}
                        />

                        <div className="form-footer-sub">
                            <button type="submit" className="form-submit" disabled={!isValid || !values.acceptTerms} onClick={() => setSubmitType('submitContinue')}>
                                Submit
                            </button>

                            <button type="submit" className="form-previous" disabled={!isValid || !values.acceptTerms} onClick={() => setSubmitType('submitCreateNew')}>
                                + Submit &amp; Create Another Order
                            </button>
                        </div>
                    </Form>
                )}
            </Formik>
            <TermsAndConditionsModal
                handleCloseModal={handleCloseTermsAndConditionsModal}
                showModal={showTermsAndConditionsModal}
                termsAndConditionsAccepted={termsAndConditionsAccepted}
                setTermsAndConditionsAccepted={setTermsAndConditionsAccepted}
            />
        </div>
    );
};

export default IntakeConfirmation;
